import { useCallback, useMemo } from "react";

import { Chip as BaseChip, type ChipProps, Spinner, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import { OPERATION_STATUS_COLORS } from "@onesolution/constants";

export default function Chip({
	isLoading,
	value,
	children,
	link,
	onClick,
	className,
	dotColor,
	classNames,
	...props
}: ChipProps & {
	isLoading?: boolean;
	value?: string | number;
	dotColor?: string;
	link?: string;
}) {
	const hasClickAction = onClick || link;
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.preventDefault();
			e.stopPropagation();
			if (onClick) return onClick(e);
			if (link) return window.open(link, "_blank");

			return undefined;
		},
		[onClick, link],
	);

	// weird but tailwind needs it :(
	const dotColorComputed = useMemo(
		() =>
			OPERATION_STATUS_COLORS[dotColor as keyof typeof OPERATION_STATUS_COLORS],
		[dotColor],
	);

	if (!isLoading && !value && !children) return null;

	return (
		<BaseChip
			size="sm"
			radius="sm"
			className={cn(
				"text-xs hover:shadow-sm transition-all",
				hasClickAction ? "cursor-pointer" : "",
				className,
			)}
			classNames={{
				content: "items-center justify-center flex relative -top-[1px]",
				dot: dotColorComputed,
				...(classNames || {}),
			}}
			endContent={
				hasClickAction ? (
					<Icon className="ml-2" icon="solar:square-share-line-linear" />
				) : null
			}
			onClick={handleClick}
			{...props}
		>
			{isLoading ? (
				<Spinner size="sm" color="default" classNames={{ wrapper: "size-3" }} />
			) : (
				value || children
			)}
		</BaseChip>
	);
}
